import forge from 'apis/forge';
import SceneConversation from 'components/Panel/Chat/Conversation/SceneConversation';
import { Loader } from 'components/Widget/Loader';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus, Quality } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveSceneChat, selectActiveScene, selectSceneChatRefs } from 'redux/db/reducers';
import { getRightmostFrameX } from 'redux/db/util';
import {
	setActiveSceneChatId,
	setGoldTooltipState,
	setSilverTooltipState,
} from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';
import { Header } from '../Header';
import * as Base from '../styles';

const SceneChatPanel = () => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const {
		chatRefs,
		activeSceneChatId,
		activeScene,
		activeChat,
		activeChatTitle,
		goldTooltipState,
		silverTooltipState,
		chatQuality,
		chatsLoading,
	} = useSelector((r: RootReducer) => {
		const chat = selectActiveSceneChat(r.db, r.mode.activeSceneChatId);
		const chatRefs = selectSceneChatRefs(r.db);
		const activeSceneChatId = r.mode.activeSceneChatId;

		// Check if activeSceneChatId is in chatRefs
		const activeChatIdInRefs = activeSceneChatId
			? chatRefs.some((ref) => ref.id === activeSceneChatId)
			: true; // If no active chat, consider it "in refs" (not loading)

		return {
			chatRefs,
			activeSceneChatId,
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			activeChat: chat,
			activeChatTitle: chat?.title || 'Untitled Chat',
			goldTooltipState: r.mode.goldTooltipState,
			silverTooltipState: r.mode.silverTooltipState,
			chatQuality: r.mode.chatQuality,
			chatsLoading:
				r.db.loading.activeScene.chats !== 'loaded' ||
				(activeSceneChatId !== null && chat === null) ||
				(activeSceneChatId !== null && !activeChatIdInRefs),
		};
	}, shallowEqual);
	const sortedChatRefs = useMemo(() => {
		// Chat refs are already sorted by updatedAt DESC from the backend
		return chatRefs;
	}, [chatRefs]);

	const moreMenuItems = useMemo(() => {
		if (!activeSceneChatId) return [];
		return [
			MenuItemAction.createRenameChat(activeSceneChatId),
			MenuItemAction.createSelectFramesFromChat(activeSceneChatId),
		];
	}, [activeSceneChatId]);

	useEffect(() => {
		// Don't auto-select when chats are loading - this includes the case when
		// a new chat is being created and hasn't appeared in chatRefs yet
		if (chatsLoading) {
			return;
		}

		const chatRefIds = sortedChatRefs.map((ref) => ref.id);
		const activeChatIdInRefs = activeSceneChatId ? chatRefIds.includes(activeSceneChatId) : false;

		if (!activeSceneChatId || !activeChatIdInRefs) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveSceneChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, activeSceneChatId, dispatch, chatsLoading]);

	// activate the gold tooltip when:
	// - only one chat is open
	// - that chat has a single user message
	// - the current quality is silver
	useEffect(() => {
		if (
			goldTooltipState === 'uninitialized' &&
			chatRefs.length === 1 &&
			chatQuality === Quality.SILVER &&
			activeSceneChatId !== null &&
			activeSceneChatId === chatRefs[0].id &&
			activeChat
		) {
			const userEntryCount =
				activeChat.entries?.filter((entry) => entry.kind === 'user').length ?? 0;

			if (userEntryCount === 1) {
				dispatch(setGoldTooltipState('visible'));
			}
		}
	}, [dispatch, goldTooltipState, chatQuality, activeSceneChatId, chatRefs, activeChat]);

	// activate the silver tooltip when:
	// - only one chat is open
	// - that chat has a single user message
	// - the current quality is gold
	useEffect(() => {
		if (
			silverTooltipState === 'uninitialized' &&
			chatRefs.length === 1 &&
			chatQuality === Quality.GOLD &&
			activeSceneChatId !== null &&
			activeSceneChatId === chatRefs[0].id &&
			activeChat
		) {
			const userEntryCount =
				activeChat.entries?.filter((entry) => entry.kind === 'user').length ?? 0;

			if (userEntryCount === 1) {
				dispatch(setSilverTooltipState('visible'));
			}
		}
	}, [dispatch, silverTooltipState, chatQuality, activeSceneChatId, chatRefs, activeChat]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = async () => {
		if (!activeScene) return;
		if (activeScene.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		const chatId = uuid();
		const rightmostFrameX = getRightmostFrameX();

		await forge.createSceneChat({
			id: chatId,
			sceneId: activeScene.id,
			title: NEW_CHAT_TITLE,
			x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
			y: 0,
			metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
		});

		dispatch(setActiveSceneChatId(chatId));

		// we intentionally do NOT update the pending chat input here.  the ChatInput component
		// owns it and manages that process - ss
	};

	return (
		<Base.Base ref={ref}>
			<Header
				title={activeChatTitle}
				onNewChat={onNewChat}
				chats={sortedChatRefs}
				activeChatId={activeSceneChatId}
				onSelectChat={(id: string) => dispatch(setActiveSceneChatId(id))}
				moreMenuItems={moreMenuItems}
			/>
			{activeSceneChatId && (
				<Base.Conversation>
					{chatsLoading ? (
						<Base.LoadingContainer>
							<Loader width={32} height={32} />
						</Base.LoadingContainer>
					) : (
						<SceneConversation />
					)}
				</Base.Conversation>
			)}
		</Base.Base>
	);
};

export default SceneChatPanel;
